import {
  ApiError,
  type CopyScriptRequest,
  CopyScriptRestEndpointService,
  type ScriptCategoryEntry,
  type ScriptEntry,
  type ScriptForm,
  ScriptImportControllerService,
  ScriptRestControllerService,
} from '@api'
import type { ExpandedState } from '@tanstack/react-table'
import { attach, createEffect, createEvent, createStore } from 'effector'

import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable.ts'
import { $creatorId } from '@/models/creators.ts'
import { editScriptsCategoryFx } from '@/models/scripts/categories.ts'
import { toast } from '@/models/toasts.ts'
import { toTree } from '@/modules/scripts/components/ScriptProvider.tsx'
import type { ScriptNode } from '@/modules/scripts/types.ts'
import { handleError } from '@/utils/error'

export const addScriptFx = attach({
  name: 'add script',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (data: ScriptForm, { creatorId }) => ({
    creatorId: creatorId!,
    requestBody: data,
  }),
  effect: createEffect(ScriptRestControllerService.addScript),
})

addScriptFx.doneData.watch(() => {
  toast.success('Script created successfully')
})

addScriptFx.failData.watch((error) => {
  handleError(error, 'Error creating script')
})

export const editScriptFx = attach({
  name: 'edit script',
  source: {
    creatorId: $creatorId,
  },
  mapParams: ({ id, ...data }: ScriptForm & { id: number }, { creatorId }) => ({
    creatorId: creatorId!,
    id,
    requestBody: data,
  }),
  effect: createEffect(ScriptRestControllerService.editScript),
})

editScriptFx.doneData.watch(() => {
  toast.success('Script updated successfully')
})

editScriptFx.failData.watch((error) => {
  handleError(error, 'Error updating script')
})

export const deleteScriptFx = attach({
  name: 'delete script',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (id: number, { creatorId }) => ({
    creatorId: creatorId!,
    id,
  }),
  effect: createEffect(ScriptRestControllerService.removeScript),
})

deleteScriptFx.doneData.watch(() => {
  toast.success('Script deleted')
})

deleteScriptFx.failData.watch((error) => {
  handleError(error, 'Error deleting script')
})

export const moveScriptFx = attach({
  name: 'move script',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    {
      updatedOrders,
      id,
    }: {
      updatedOrders: Record<number, number[]>
      id: number
    },
    { creatorId },
  ) => ({
    id,
    creatorId: creatorId!,
    requestBody: updatedOrders,
  }),
  effect: createEffect(ScriptRestControllerService.moveScript),
})

moveScriptFx.failData.watch((error) => {
  handleError(error, 'Error moving script')
})

export const importScriptsFx = attach({
  name: 'import scripts',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (entries: Array<ScriptEntry>, { creatorId }) => ({
    creatorId: creatorId!,
    requestBody: { entries },
  }),
  effect: createEffect(ScriptImportControllerService.importScript),
})

importScriptsFx.failData.watch((error) => {
  handleError(error, 'Error importing scripts')
})

export const copyScriptsFx = createEffect({
  name: 'copy scripts',
  handler: async (requestBody: CopyScriptRequest) => {
    return CopyScriptRestEndpointService.copy({ requestBody })
  },
})

copyScriptsFx.doneData.watch(() => {
  toast.success('Successfully copied script to creator')
})

copyScriptsFx.failData.watch((error) => {
  handleError(error, 'Error copying script to creator')
})

export const getScriptsFx = attach({
  name: 'get scripts',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (_: void, { creatorId }) => ({ creatorId: creatorId! }),
  effect: createEffect(ScriptRestControllerService.listScripts),
})

getScriptsFx.fail.watch(({ error }) => {
  handleError(error, 'Something went wrong while getting scripts', getScriptsFx)
})

export const setScriptCategories = createEvent<Entry<ScriptNode>[]>(
  "set script's categories",
)
export const $scriptCategories = createStore<Entry<ScriptNode>[]>([], {
  name: 'script categories',
})
  .on(getScriptsFx.doneData, (_, { categories }) =>
    (categories! as DeepRequired<ScriptCategoryEntry>[])
      .map((it) => toTree(it))
      .sort((l, r) => l.orderNumber - r.orderNumber),
  )
  .on(setScriptCategories, (_, scripts) => scripts)
  .on(editScriptsCategoryFx.done, (store, { params: { name, id } }) =>
    store.map((item) => (item.id === id ? { ...item, name } : item)),
  )

export const $scriptsError = createStore<Nullable<ApiError>>(null, {
  name: 'script categories error',
})
  .on(getScriptsFx.failData, (_, error) => error as ApiError)
  .reset(getScriptsFx)

export const setScriptsExpansion = createEvent<ExpandedState>(
  'set scripts expansion',
)
export const unexpandFolder = createEvent<string>('unexpand scripts folder')
export const $scriptsExpanded = createStore<ExpandedState>({})
  .on(setScriptsExpansion, (_, value) => value)
  .on(unexpandFolder, (store, id) => {
    if (typeof store === 'object' && id in store) {
      const newValue = { ...store }

      delete newValue[id]

      return newValue
    }

    return store
  })
